// web-v2/src/lib/quoteWizardModel.js
//
// The wizard's model layer: the loaded quote turned into a form, the form turned
// back into per-step PATCH bodies, and the two soft checks it shows along the
// way. QUOTE-WIZARD-SPEC phase 1.
//
// It lives apart from QuoteWizardSheet.jsx so the payload rules are testable
// without a browser. The rules worth testing are the ones that would be silent
// if they broke: which keys a step writes, that `carrier` is in none of them,
// that an unanswered TRIA writes nothing at all, and that a blank field arrives
// as null rather than zero.
//
// Pure ESM, React-free, so tests/quote-wizard.test.js can import it in node.

import {
    coveragePartKey, toNumber, fmtMoney,
    parsePilotRequirements, encodePilotRequirements, buildCommonRollup,
} from './quoteFields.js';
import { axesSignature } from './quoteAxes.js';

// ── THE AXES, AS THE FORM HOLDS THEM (2026-08-07) ─────────────────────────
//
// The broker edits N liability limits and M medical-payments limits and the
// server stores the N x M combinations. What the FORM holds is those two lists,
// and it holds them in a deliberately unglamorous shape:
//
//     leg 0 IS the existing scalar fields. Legs 2..N live in an array.
//
// That is ONE HOME PER LEG, not two homes for one leg, and it is written this
// way on purpose. `liability_per_occurrence` and its siblings are already wired
// into coverageDefaults, into the `defaulted` marking that tells a value nobody
// confirmed from one the carrier stated, and into completenessInput. Moving leg
// 0 into an array would have rewritten all three to change nothing the broker
// can see, on a quote-entry path that reaches signed paper.
//
// A quote with one liability limit and one medical row is therefore EXACTLY the
// form it has always been, and 365 of the 371 quotes carrying options are that.
export function liabilityLegsOf(f) {
    return [
        {
            per_occurrence: f.liability_per_occurrence,
            per_passenger: f.liability_per_passenger,
            premium: f.liability_premium,
            tria_premium: f.tria_premium,
            // Leg 0's smooth/split is the scalar the form has always carried.
            smooth: f.liability_smooth ?? null,
            key: f.liability_leg_key || null,
        },
        // SMOOTH OR SPLIT IS EACH LEG'S OWN ANSWER (David, 2026-09-09). It was
        // stamped onto every leg from one toggle, so a quote could not offer
        // $1M CSL beside $1M/$100K. The store never had that limit:
        // quote_options.liability_smooth is per corner and liabilityKey() keeps
        // two legs that differ only on smooth apart. A leg that has not said
        // is null, never leg 0's answer ("fork can be blank ... no copy").
        ...(Array.isArray(f.liability_legs_extra) ? f.liability_legs_extra : [])
            .map((l) => ({ ...l, smooth: l.smooth ?? null })),
    ];
}

export function medicalLegsOf(f) {
    return [
        { per_person: f.medical_per_person, premium: f.medical_premium, key: f.medical_leg_key || null },
        ...(Array.isArray(f.medical_legs_extra) ? f.medical_legs_extra : []),
    ];
}

// WHETHER THIS SAVE SPEAKS FOR A GRID. Three ways in: the broker has added a
// leg, or the quote already carries more than one corner (so removing a leg has
// to delete rows), or neither, in which case the ordinary single-option save is
// what runs and nothing about it changes.
//
// `axes_ok === false` means the stored options are separate offers rather than
// points on a grid (Global's All Risk / GNIM trio, the two Acceleration
// per-aircraft sets, Allianz's pair that records its limits only in the label).
// Those are edited one at a time and never go through this path.
export function usesAxes(f) {
    if (f?.axes_ok === false) return false;
    const extra = (f?.liability_legs_extra?.length || 0) + (f?.medical_legs_extra?.length || 0);
    return extra > 0 || Number(f?.corner_count || 0) > 1;
}

// Did the broker actually MOVE a limit. Every step of this wizard sends every
// field it holds, which is how a sheet left open across an outside write
// restores its own stale copy (it ate a $60 med-pay premium twice on
// 2026-08-07). The corner set is the most destructive thing on the page — a
// stale write DELETES rows — so the axes ride along only when they differ from
// what was loaded.
export function axesChanged(f) {
    if (!f) return false;
    return axesSignature(liabilityLegsOf(f), medicalLegsOf(f), f.hull_premium) !== f.axes_loaded_signature;
}

export const STEP_HINTS = {
  offer: 'What the carrier came back with.',
  // TRIA lost its own page on 2026-08-01 and its hint with it. The sentence
  // survives as the block's own heading at the foot of this step.
  terms: 'What is actually covered, terrorism included.',
  pilots: 'Who may fly it.',
  rest: 'Everything else. All optional.',
};

// THE FOUR TRIA STATES (wave 2 item 1, 2026-07-30). David: "many P&B policies
// don't include TRIA. some do at additional premium. some will add it
// optionally." The boolean had no room for a policy that simply excludes
// terrorism cover, which on P&B is the common answer, so the most frequent real
// answer was the one the schema could not record.
//
// NULL is not in this list and never will be: it is the absence of an answer,
// and 'excluded' IS an answer. Conflating them is the same error this whole
// feature exists to prevent.
export const TRIA_STATUSES = ['included', 'optional', 'excluded'];

// A QUOTE'S MONEY IS NOT ONE NUMBER (prompt 101). Three kinds, and they behave
// differently:
//
//   base premium     hull + liability + medical. This is `total_premium`.
//   elected options  TRIA, war, Mexican liability. Taken or not taken, priced
//                    separately, and ADDED ON TOP at election time — the Tower
//                    card renders total_premium + electedPartsTotal.
//   fees             the program admin fee. Payable by the client, earns the
//                    agency nothing, and is not premium at all.
//
// THIS CHECK USED TO SUM AN ELECTION INTO A TOTAL DEFINED TO EXCLUDE IT, and to
// omit medical, which the total includes. Two errors that partly cancelled.
// David, on Mach 2: "Hull + Liability + TRIA = $9,207 vs Total $8,932 (delta
// $275)" — and $275 is exactly the TRIA. The parts and the total were not
// disagreeing about arithmetic, they were disagreeing about what belongs in a
// total.
//
// Measured on the book before changing it: of 93 options carrying a breakdown,
// 53 warned. ALL 22 options that carry a medical premium warned, and all 22 stop
// warning once medical is counted — the check had never once been right on a
// quote with a medical premium. 4 more warned by exactly their TRIA amount. A
// guard that cries wolf on 57% of its subjects gets ignored, which is how a real
// mismatch gets missed.
//
// So: BASE COMPARES TO BASE. TRIA and the fee are excluded by definition, not by
// tolerance, and the message names what it summed so the next reader does not
// have to guess.
export function premiumDelta(total, hull, liability, medical) {
  const tot = toNumber(total);
  const hp = toNumber(hull);
  const lp = toNumber(liability);
  const mp = toNumber(medical);
  if (tot == null) return null;
  if (hp == null && lp == null && mp == null) return null;
  const sum = (hp || 0) + (lp || 0) + (mp || 0);
  const delta = Math.abs(sum - tot);
  if (delta > 50) {
    return { kind: 'warn', message: `Hull + Liability + Medical = ${fmtMoney(sum)} vs base premium ${fmtMoney(tot)} (delta ${fmtMoney(delta)})` };
  }
  return { kind: 'ok', message: `Breakdown matches the base premium within ${fmtMoney(delta)}` };
}

// OPW soft-warn, unchanged from QuoteEntrySheet section 2.5: a partially filled
// structured warranty understates what the client sees on the Tower.
export function opwWarning(f) {
  if (f.named_pilots_only) return null;
  const hasType = !!String(f.opw_type_hours || '').trim();
  const hasMM = !!String(f.opw_make_model_hours || '').trim();
  const hasTotal = !!String(f.opw_total_hours || '').trim();
  if ((hasType || hasMM) && !hasTotal) {
    return 'Type or make/model hours are set with no total-hours floor.';
  }
  const ft = String(f.opw_full_text || '').trim();
  const anyStructured = hasType || hasMM || hasTotal || !!f.opw_certificate || (f.opw_ratings || []).length > 0;
  if (ft && anyStructured) {
    const ftNums = (ft.match(/\d+/g) || []).map(Number).filter((n) => n >= 5);
    const structuredNums = [f.opw_total_hours, f.opw_make_model_hours, f.opw_type_hours]
      .flatMap((v) => (String(v || '').match(/\d+/g) || []).map(Number));
    const uncaptured = [...new Set(ftNums.filter((n) => !structuredNums.includes(n)))];
    if (uncaptured.length) {
      return `The full clause mentions ${uncaptured.slice(0, 3).join(', ')}, which are not in the fields above. The client sees only the fields.`;
    }
  }
  return null;
}

// COVERAGE-STEP DEFAULTS (item 5, 2026-07-30). David: "good to default to
// $1M/$100K/$5K, but also coverage type and aircraft use should default."
//
// A DEFAULT IS A VALUE, NOT AN ANSWER, and this file only supplies the value.
// The wizard records which fields came from here and marks them on screen until
// he touches one, because "$1,000,000, nobody has confirmed it" and
// "$1,000,000, the carrier said so" are different facts and a form that shows
// them identically is the absence-as-evidence bug in a new coat.
//
// The three liability figures are CONSTANTS on purpose, per the brief: they are
// the limits nearly every P&B quote carries, and a deal that applied for $2M
// does not change what the wizard offers before the carrier has spoken.
export const LIABILITY_DEFAULTS = {
    liability_per_occurrence: '1000000',
    liability_per_passenger: '100000',
    medical_per_person: '5000',
};
export const FALLBACK_COVERAGE_TYPE = 'Full Flight';
export const FALLBACK_AIRCRAFT_USE = 'Pleasure & Business';

// WHAT THE DATA ALREADY KNOWS (2026-08-01). Both figures are the live book, not
// a guess: 361 of 364 option rows are 12 months (all three exceptions are
// six-month Beacon rows), and 163 of the 171 quotes that record an open-pilot
// certificate say Private, with 3 more saying "Private + Instrument".
//
// HARDCODED, AND SAID OUT LOUD. These belong in the TRACON admin config rather
// than in this file, the same way the tax rates do. That is a second surface and
// a fetch on wizard open, so it is deferred deliberately rather than forgotten.
// Until then, changing either one is a code change.
//
// A DEFAULT HERE IS NEVER SILENT. Every key coverageDefaults returns lands in
// the wizard's `defaulted` set and renders a "default" tag beside its label that
// disappears the moment the broker touches the field. That is the whole reason
// this file is allowed to fill anything in: an unmarked default is a claim
// nobody made, and it rides to paper.
export const TERM_DEFAULT = '12';
export const OPW_CERT_DEFAULT = 'Private';

// A DEFAULT ON A FIELD THE PRESENT GATE ASKS IS NEVER SAVED UNTIL HE TOUCHES IT
// (decision 12, "required means STATED"; decision 14, imports keep what the
// carrier sent; final review PG-1). Opening an item from the Present sheet and
// closing it saved these constants as answers and the gate then passed on them.
// They still fill the screen, tagged "default"; a key he touches leaves the
// defaulted set and saves as his answer. Absent from the body means the stored
// value stands (a blank stays blank, so the gate keeps listing it).
export const UNSAVED_DEFAULTS = Object.freeze(['coverage_type', 'liability_per_occurrence', 'liability_per_passenger',
    'medical_per_person', 'term_months', 'opw_certificate', 'proposed_effective']);
// A PER-PASSENGER FIGURE IS THE SPLIT ANSWER. A smooth CSL has no sublimit, so a
// per-passenger amount stated on a leg nobody had called smooth or split can only
// mean split. Without this the field took $100,000, showed it, and the terms save
// dropped it (an unanswered shape writes neither column): David, 2026-10-24,
// "I can't get the $100k per pax liability to show up". A blank leaves it alone.
export function withImpliedSplit(leg, patch) {
    if (!patch || !('per_passenger' in patch)) return patch;
    if (leg && leg.smooth != null) return patch;
    if (!String(patch.per_passenger ?? '').trim()) return patch;
    return { ...patch, smooth: false };
}

// LEAVING A STEP WITH UNTOUCHED DEFAULTS ASKS (David, 2026-10-24: "an untouched
// default brings up a 'confirm default limits?' upon save & close or next
// click"). The defaults above are still never saved unasked; the ask is how a
// default that matches the quote becomes his answer without retyping it.
// Which step renders which default, so a save asks only about its own page.
export const DEFAULT_KEYS_BY_STEP = Object.freeze({
    offer: ['term_months', 'proposed_effective'],
    terms: ['coverage_type', 'liability_per_occurrence', 'liability_per_passenger', 'medical_per_person'],
    pilots: ['opw_certificate'],
});
export const DEFAULT_LIMIT_KEYS = Object.freeze(['liability_per_occurrence', 'liability_per_passenger', 'medical_per_person']);

// The untouched defaults this save would leave unstated, minus any he already
// chose to leave. A per-passenger default under a smooth limit is not on screen.
export function defaultsToAsk(stepKey, form, defaulted, declined) {
    if (!form || !defaulted || typeof defaulted.has !== 'function') return [];
    const left = declined && typeof declined.has === 'function' ? declined : new Set();
    return (DEFAULT_KEYS_BY_STEP[stepKey] || []).filter((k) => defaulted.has(k) && !left.has(k)
        && String(form[k] ?? '').trim() !== ''
        && !(k === 'liability_per_passenger' && form.liability_smooth === true));
}

// His yes: the values as they stand, as answers. A confirmed per-passenger
// default is the split answer, the same rule as typing it (withImpliedSplit).
export function confirmDefaultsPatch(form, keys) {
    const patch = {};
    for (const k of keys || []) patch[k] = form[k];
    if (patch.liability_per_passenger != null && form.liability_smooth == null) patch.liability_smooth = false;
    return patch;
}

export function withoutUntouchedDefaults(body, defaulted) {
    if (!body || !defaulted || typeof defaulted.has !== 'function') return body;
    const out = { ...body };
    for (const k of UNSAVED_DEFAULTS) if (defaulted.has(k)) delete out[k];
    return out;
}

// Only fields the quote has NOT answered. Returns the patch, never the merged
// form, so the caller can record exactly which keys it filled in.
export function coverageDefaults(form, prefill) {
    const blank = (v) => !String(v ?? '').trim();
    const patch = {};
    for (const [k, v] of Object.entries(LIABILITY_DEFAULTS)) {
        if (blank(form[k])) patch[k] = v;
    }
    if (blank(form.coverage_type)) {
        patch.coverage_type = String(prefill?.coverage_type || '').trim() || FALLBACK_COVERAGE_TYPE;
    }
    // The carrier's own standard wording (wave 2 item 5). There is NO constant
    // fallback here on purpose: a carrier with no standard territory leaves the
    // field blank, and blank is a state. Writing "US" for the thirteen carriers
    // that have no wording would put words in their mouth on a term that
    // decides whether a claim in Mexico is covered.
    if (blank(form.territory)) {
        const carrierDefault = String(prefill?.carrier_default_territory || '').trim();
        if (carrierDefault) patch.territory = carrierDefault;
    }
    // The carrier's standard enhancement wording (2026-07-31). David was
    // retyping the same paragraph on every quote. Identical rules to the
    // territory above: NO constant fallback, because a carrier with no standard
    // wording leaves the field blank and blank is a state. It fills only a BLANK
    // field, so text he already wrote is never overwritten.
    if (blank(form.coverage_enhancements)) {
        const carrierEnh = String(prefill?.carrier_default_enhancements || '').trim();
        if (carrierEnh) patch.coverage_enhancements = carrierEnh;
    }
    if (blank(form.aircraft_usage)) {
        // The deal's own as-applied-for use, taken off its aircraft because
        // there is no deal-level field for it. Unanimous across the fleet is
        // the deal's answer; a fleet that disagrees has no single answer, and
        // the constant is at least visibly a guess rather than one tail's value
        // silently applied to all of them.
        //
        // THIS BRANCH HAD NEVER EXECUTED. It read prefill.aircraft_schedule and
        // a.aircraft_use; the quote-prefill route sends `aircraft` whose rows
        // carry `current_aircraft_use` (routes/tracon.routes.js:10016-10026).
        // Two key mismatches, so `uses` was always empty and every quote took
        // the constant. Right about 94% of the time it fires, and silently
        // wrong on exactly the risks where use is not cosmetic: Industrial Aid,
        // Instruction & Rental, Flying Club, Charter.
        //
        // aircraft_schedule IS a real key, in the other direction: it is what
        // the wizard SENDS (schedulePayload). It is not what prefill returns.
        const uses = [...new Set((prefill?.aircraft || [])
            .map((a) => String(a?.current_aircraft_use || '').trim()).filter(Boolean))];
        patch.aircraft_usage = uses.length === 1 ? uses[0] : FALLBACK_AIRCRAFT_USE;
    }
    if (blank(form.term_months)) patch.term_months = TERM_DEFAULT;
    // NOT ON A NAMED-PILOTS-ONLY QUOTE. NPO means the open pilot fields are
    // cleared on save and hidden on screen, so defaulting a certificate there
    // would put a value into a field the quote is about to blank, in a section
    // the broker cannot see to correct.
    if (form.named_pilots_only !== true && blank(form.opw_certificate)) {
        patch.opw_certificate = OPW_CERT_DEFAULT;
    }
    // THE RENEWAL EFFECTIVE DATE. The route resolved this already: it is
    // Policy_Start when the deal carries one, else the PRIOR policy's
    // Policy_End_Date read through getPriorPolicy, and it arrives with
    // effective_source saying which. A renewal's effective date IS the day the
    // expiring policy ends.
    //
    // THIS IS NOT THE +1YR BUG (03086cb, 10 corrupted binds). That default
    // COMPUTED a date by adding a year to something. This one copies a date the
    // expiring policy actually records, and it fills only a BLANK field, so a
    // quote that states its own effective date is never touched. It is marked
    // defaulted like everything else here, so an unconfirmed one is visible on
    // screen. tests/quote-wizard-defaults.test.js pins the renewal case.
    // `prefill.deal.default_effective`, NOT `prefill.default_effective`. The
    // route nests it under `deal` alongside effective_source and is_renewal, and
    // a flat read here would have resolved undefined on every quote and defaulted
    // nothing, silently, forever.
    if (blank(form.proposed_effective)) {
        const fromDeal = String(prefill?.deal?.default_effective || '').trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(fromDeal)) patch.proposed_effective = fromDeal;
    }
    return patch;
}

// TAXES AND FEES HAVE ONE HOME (item 8c, 2026-07-30). The offer step asked for
// them, and so did every tail, with nothing reconciling the two. Type $400 in
// the header and $200 on each of two tails and the quote carries two different
// answers to "what tax is on this policy", one of which reaches a client
// statement.
//
// Where there are rows to add up, the ROWS are the truth and the header is
// their sum. Returns null when there is nothing to derive from (one aircraft,
// or none), and the header stays his to type in.
//
// A SUM OF NOTHING IS NOT ZERO. A fleet where no tail states a tax derives
// null, so the header stays blank. $0 of tax is a claim, and a wrong one.
export function derivedTaxesAndFees(rows) {
    if (!Array.isArray(rows) || rows.length < 2) return null;
    const sum = (key) => {
        const vals = rows.map((r) => toNumber(r?.[key])).filter((v) => v != null);
        return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
    };
    return { estimated_taxes: sum('estimated_taxes'), admin_fee: sum('fees') };
}

// The loaded quote -> the wizard's one form object. Blank stays blank: every
// field that the row does not carry arrives here as '' or null and renders empty.
export function formFromDetail(q) {
  const parsed = parsePilotRequirements(q.pilot_requirements || '');
  // THE AXES COME OFF THE SERVER, DERIVED THERE (2026-08-07). getQuoteDetail
  // runs deriveAxes against the rows and applyQuoteAxes enforces the same rule
  // against the same rows through the same module. The wizard renders what it
  // was given and derives no part of the grid itself.
  const axes = q.axes && typeof q.axes === 'object' ? q.axes : { ok: true, liability: [], medical: [] };
  const gridded = axes.ok !== false && (axes.liability || []).length > 0;
  const L = gridded ? axes.liability : [];
  const M = gridded ? (axes.medical || []) : [];
  const s = (v) => (v == null ? '' : String(v));
  // ON A GRID, LEG 0 IS THE FIRST LEG OF THE AXIS, not whichever option happened
  // to be flattened. Those are the same row on the 365 single-corner quotes and
  // they are NOT the same row on Milam's Mach 2, where the flattened primary can
  // be the $2M corner while the axis reads $1M then $2M. Seeding from the
  // flattened option there would have shown $2M twice and lost the $1M leg.
  const liab0 = L[0] || null;
  const med0 = M[0] || null;
  const form = {
    // WHICH OPTION THIS FORM IS SHOWING (prompt 131). Carried, never edited, and
    // echoed back on every save so the server writes the option the broker was
    // looking at instead of resolving the target a second time.
    //
    // The two resolutions used to be independent: getQuoteDetail collapsed to a
    // primary to build this form, updateBrokerQuote collapsed again to decide
    // where to write, and nothing compared them. On 2026-08-06 they disagreed on
    // a live quote and option 1 received option 2's premium.
    option_id: q.option_id || null,
    // The label of the option being edited (prompt 139). Editable per option:
    // it was only ever settable on the add-option form, so option 1 could never
    // be named and 105 of 380 options carry a label that identifies nothing.
    option_label: q.option_label || '',
    premium: q.premium != null ? String(q.premium) : '',
    estimated_taxes: q.estimated_taxes != null ? String(q.estimated_taxes) : '',
    admin_fee: q.admin_fee != null ? String(q.admin_fee) : '',
    hull_premium: q.hull_premium != null ? String(q.hull_premium) : '',
    liability_premium: liab0 ? s(liab0.premium)
      : (q.liability_premium != null ? String(q.liability_premium) : ''),
    // The third leg of the base. A column since the table was built, written
    // only by the IAT/Global parsers (23 of 375 options) because no broker
    // surface ever asked for it, and David's base premium includes it: "if I
    // never offer, I'll never bind that coverage."
    medical_premium: med0 ? s(med0.premium)
      : (q.medical_premium != null ? String(q.medical_premium) : ''),

    // ── THE AXES ───────────────────────────────────────────────────────────
    // Leg 0 is the block of scalars above; these are legs 2..N and the loaded
    // identity of leg 0, which is how an EDIT of a limit ($2M -> $3M) is told
    // from a DELETE of one limit and an ADD of another. Matching on the
    // coordinate instead would drop the row's id, its presented flag and the
    // client's selection every time a number changed.
    liability_leg_key: liab0?.key || null,
    medical_leg_key: med0?.key || null,
    liability_legs_extra: L.slice(1).map((l) => ({
      per_occurrence: s(l.per_occurrence),
      per_passenger: s(l.per_passenger),
      premium: s(l.premium),
      tria_premium: s(l.tria_premium),
      // The leg's OWN smooth/split answer. Dropped here until 2026-09-09, so a
      // stored per-corner answer died on load and leg 0's was stamped over it.
      smooth: l.smooth == null ? null : l.smooth === true,
      key: l.key || null,
    })),
    medical_legs_extra: M.slice(1).map((m) => ({
      per_person: s(m.per_person),
      premium: s(m.premium),
      key: m.key || null,
    })),
    // WHAT THE STORED TOTALS IMPLY for a leg that records no premium of its own,
    // keyed by leg. Offered as a chip and never written: Milam's Mach 2 records
    // medical_premium on ONE of the four corners, so the $10,000 leg has no
    // figure of its own while the totals say 120 four times over. Same rule as
    // the tax chip beside it — a derived number the broker takes, or does not.
    axes_implied: Object.fromEntries([
      ...L.filter((l) => l.premium_implied != null).map((l) => [l.key, l.premium_implied]),
      ...M.filter((m) => m.premium_implied != null).map((m) => [m.key, m.premium_implied]),
    ]),
    // false means the stored options are separate offers, not points on a grid,
    // and the sheet falls back to editing them one at a time.
    axes_ok: axes.ok !== false,
    axes_reason: axes.reason || '',
    corner_count: axes.corner_count || 0,
    // Set below, once the legs exist: the corner set is only rewritten when the
    // broker actually moved a limit.
    axes_loaded_signature: '',
    // Whether this carrier writes short-term. Gates the six-month CONTROL and
    // never a value; NULL on the carrier arrives here as false.
    carrier_offers_short_term: q.carrier_offers_short_term === true,

    quote_number: q.carrier_quote_number || '',
    proposed_effective: (q.proposed_effective || '').slice(0, 10),
    quote_expires_at: (q.quote_expires_at || '').slice(0, 10),

    coverage_type: q.coverage_type || '',
    aircraft_usage: q.aircraft_usage || '',
    // READ ONLY here. quote_options.hull_value is DERIVED from the first
    // aircraft line, and there is no body key that writes it directly, so a
    // quote with a hull and no line rows (the shape a portal-logged quote lands
    // in) can display its hull but cannot edit it in this surface. Carried so
    // completeness does not report a hull the quote plainly has as missing.
    hull_value: q.hull_value != null ? String(q.hull_value) : '',
    // NULL means the row never said. It stays null and the toggle stays unlit.
    liability_smooth: liab0 ? (liab0.smooth == null ? null : liab0.smooth === true)
      : (q.liability_smooth == null ? null : q.liability_smooth === true),
    liability_per_occurrence: liab0 ? s(liab0.per_occurrence)
      : (q.liability_per_occurrence != null ? String(q.liability_per_occurrence) : ''),
    liability_per_passenger: liab0 ? s(liab0.per_passenger)
      : (q.liability_per_passenger != null ? String(q.liability_per_passenger) : ''),
    medical_per_person: med0 ? s(med0.per_person)
      : (q.medical_per_person != null ? String(q.medical_per_person) : ''),
    in_motion_deductible: q.in_motion_deductible || '',
    non_motion_deductible: q.non_motion_deductible || '',
    territory: q.territory || '',

    // A boolean is an answer; anything else (null, undefined, a legacy string)
    // is the absence of one and stays absent. Written as a typeof test rather
    // than a null comparison because tests/quote-tria-answer.test.js bans the
    // shape where this field is assigned the result of a null test, and it is
    // right to: that shape is one character away from the inversion that turns
    // "we found no TRIA line" into "TRIA is included".
    tria_status: TRIA_STATUSES.includes(q.tria_status) ? q.tria_status : null,
    tria_premium: q.tria_premium != null ? String(q.tria_premium) : '',

    named_pilots_only: q.named_pilots_only === true,
    opw_certificate: q.opw_certificate || '',
    opw_ratings: Array.isArray(q.opw_ratings) ? q.opw_ratings : [],
    opw_total_hours: q.opw_total_hours != null ? String(q.opw_total_hours) : '',
    opw_type_hours: q.opw_type_hours || '',
    opw_make_model_hours: q.opw_make_model_hours != null ? String(q.opw_make_model_hours) : '',
    opw_full_text: q.opw_full_text || '',
    pilot_requirements_by_name: parsed.byName,
    // Requirements that apply to EVERY pilot, each scoped to a tail or to the
    // whole policy. The COLUMN is canonical; the common region of
    // pilot_requirements is its display rollup. A quote that never set the
    // column (null) shows its legacy common text as one All-aircraft row, so
    // nothing typed before this existed is lost, and saving promotes it.
    common_requirements: Array.isArray(q.common_requirements)
      ? q.common_requirements.map((r) => ({
        tail_number: r?.tail_number || '',
        requirement: r?.requirement || '',
      }))
      : (parsed.common ? [{ tail_number: '', requirement: parsed.common }] : []),
    // The carrier's DECISION on each pilot, which is a different fact from the
    // requirement it may attach (a pilot can be approved and still owe 25 hours
    // dual). Keyed by contact_id, exactly as the entry sheet keeps it.
    // `loaded_status` is what the ROW said when it was read, carried so the
    // encoder can tell a value it is preserving from one the broker just chose
    // (item 9). It is never sent back as a field.
    pilot_approval_by_contact: Object.fromEntries(
      (Array.isArray(q.pilot_approval_states) ? q.pilot_approval_states : [])
        .filter((a) => a && String(a.contact_id || '').trim())
        .map((a) => [
          String(a.contact_id),
          {
            status: a.status || '',
            condition_text: a.condition_text || '',
            name: a.name || '',
            loaded_status: a.status || '',
            // Excluded on some aircraft, named on the rest (2026-09-30).
            excluded_tails: Array.isArray(a.excluded_tails) ? a.excluded_tails.filter(Boolean) : [],
          },
        ])
    ),
    // EXCLUDED BY THE CARRIER, NOT ON THE ROSTER (David, 2026-09-30: "should be
    // able to just add an excluded pilot without adding to the roster"). A
    // carrier can exclude a person who is not a pilot on the deal at all, so a
    // state row may carry a name and no contact id. Kept apart from the
    // by-contact map, whose key is the contact id, and written back beside it.
    carrier_excluded_names: (Array.isArray(q.pilot_approval_states) ? q.pilot_approval_states : [])
      .filter((a) => a && !String(a.contact_id || '').trim() && String(a.status || '').trim() === 'excluded')
      .map((a) => ({ name: a.name || '', condition_text: a.condition_text || '' })),

    underwriter: q.underwriter || '',
    // CLIENT-FACING prose. It renders on the Tower quote card and copies to the
    // Deal at bind, unlike broker_notes, which is internal.
    coverage_enhancements: q.coverage_enhancements || '',
    term_months: q.term_months != null ? String(q.term_months) : '',
    subject_to: q.subject_to || '',
    broker_notes: q.broker_notes || '',
    coverage_parts: (Array.isArray(q.coverage_parts) ? q.coverage_parts : []).map((row) => {
      const pctOf = row.premium_pct_of || '';
      const prem = row.premium ?? row.Premium;
      return {
        id: row.id || undefined,
        key: row.key || undefined,
        part_type: row.part_type || row.Part_Type || '',
        limit: (row.limit ?? row.Limit) != null ? String(row.limit ?? row.Limit) : '',
        sub_limit: (row.sub_limit ?? row.Sub_Limit) != null ? String(row.sub_limit ?? row.Sub_Limit) : '',
        broker_notes: row.broker_notes || row.Broker_Notes || '',
        client_blurb: row.client_blurb || '',
        optional: (row.optional ?? row.Optional) === true,
        elected_default: row.elected_default === true,
        price_mode: pctOf || 'flat',
        pct: pctOf && row.pct != null ? String(Number(row.pct) * 100) : '',
        premium: !pctOf && prem != null ? String(prem) : '',
      };
    }),
  };
  // Taken from the assembled form, so what is compared on save is exactly what
  // was loaded, through the same accessors and the same normalisation.
  form.axes_loaded_signature = axesSignature(liabilityLegsOf(form), medicalLegsOf(form), form.hull_premium);
  return form;
}

// The per-aircraft rows, seeded from the canonical snake_case aircraft_coverage
// (Supabase, not the CRM-shape mirror). Same rows QuoteDeltaSheet edited, so the
// per-row state taxes and fees it collected round-trip through the wizard rather
// than being dropped when it takes over that door.
//
// OFF THE FLEET (Docjosh, 2026-09-09). A quote written before an aircraft left
// the deal still carries that aircraft in its schedule, and the tail label
// comes from the LIVE fleet, so the line used to degrade to a bare make/model
// with nothing saying the aircraft was gone and no way to take it off the
// quote. `off_fleet` is true only when the prefill's live fleet IS a list and
// the aircraft is not on it; a prefill that failed to load is UNKNOWN (null),
// because an empty result and a failed call are not the same thing. The
// retired list (policy_aircraft.retired_at, sent by quote-prefill) supplies
// the tail and the reason the live list no longer has.
export function rowsFromDetail(q, prefill) {
  const live = Array.isArray(prefill?.aircraft) ? prefill.aircraft : null;
  const tailById = new Map((live || []).map((a) => [String(a.tail_number_id), a]));
  const retiredById = new Map((Array.isArray(prefill?.retired_aircraft) ? prefill.retired_aircraft : [])
    .map((a) => [String(a.tail_number_id), a]));
  return (Array.isArray(q.aircraft_coverage) ? q.aircraft_coverage : []).map((r) => {
    const id = String(r.tail_number_id);
    const ref = tailById.get(id) || {};
    const offFleet = live ? !tailById.has(id) : null;
    const retired = offFleet === true ? (retiredById.get(id) || null) : null;
    return {
      tail_number_id: r.tail_number_id,
      tail_display: ref.tail_display || retired?.tail_display || r.tail_display || r.make_model || '',
      off_fleet: offFleet,
      retired_at: retired?.retired_at || null,
      retired_reason: retired?.retired_reason || null,
      replaced_by_tail: retired?.replaced_by_tail || null,
      year: r.year ?? ref.year ?? '',
      make_model: r.make_model || ref.make_model || '',
      hull_value: r.hull_value != null ? String(r.hull_value) : '',
      coverage_type: r.coverage_type || '',
      aircraft_use: r.aircraft_use || '',
      aircraft_premium: r.aircraft_premium != null ? String(r.aircraft_premium) : '',
      deductible_in_motion: r.deductible_in_motion || '',
      deductible_not_in_motion: r.deductible_not_in_motion || '',
      estimated_taxes: r.estimated_taxes != null ? String(r.estimated_taxes) : '',
      fees: r.fees != null ? String(r.fees) : '',
      // === false on purpose: absence means the aircraft IS on the offer, and
      // an unreadable row must never be read as excluded.
      on_quote: r.on_quote === false ? false : undefined,
    };
  });
}

// The rows as the API takes them. Blank stays blank: '' becomes null rather than
// zero, so a deductible nobody entered does not arrive as a $0 deductible.
//
// UNDEFINED WHEN THERE ARE NO ROWS, and this is the important line in the file.
// mapBodyToOption treats a present aircraft_schedule as the whole truth: it
// overwrites quote_options.aircraft_coverage with what it is given and re-derives
// hull_value from the first row. Sending [] therefore does not mean "no change",
// it means "this quote covers no aircraft", and it nulls the hull value. Caught
// on a live render check against AsmanAir, where the quote carried a $250,000
// hull with no line rows and walking through the terms step erased it.
export function schedulePayload(rows) {
  const eligible = rows.filter((r) => r.tail_number_id);
  if (!eligible.length) return undefined;
  return eligible.map((r) => ({
    tail_number_id: r.tail_number_id,
    make_model: r.make_model || null,
    year: r.year || null,
    hull_value: r.hull_value === '' ? null : toNumber(r.hull_value),
    coverage_type: r.coverage_type || null,
    aircraft_use: r.aircraft_use || null,
    aircraft_premium: r.aircraft_premium === '' ? null : toNumber(r.aircraft_premium),
    deductible_in_motion: r.deductible_in_motion || null,
    deductible_not_in_motion: r.deductible_not_in_motion || null,
    estimated_taxes: r.estimated_taxes === '' ? null : toNumber(r.estimated_taxes),
    fees: r.fees === '' ? null : toNumber(r.fees),
    // WHICH AIRCRAFT THIS OFFER COVERS, carried straight back out. The server
    // treats a present aircraft_schedule as the whole truth and overwrites the
    // stored one, so a marker this payload forgets is a marker DELETED: the
    // excluded aircraft silently rejoins the offer and the total goes back to
    // describing airplanes the client is not buying. Same shape as the per-leg
    // smooth answer that died on load (2026-09-09). Only false is ever sent,
    // because "on" is spelled as absence.
    ...(r.on_quote === false ? { on_quote: false } : {}),
  }));
}

// APPROVED AND CONDITIONAL ARE ONE SELECTION (item 9, 2026-07-30). David:
// "seems like approved and conditional can be filed away into one selection
// though. if the training box is filled in, it's conditional... but that's still
// an approval". The old control made him say it twice and did not check that
// the two agreed.
//
// The COLUMN is unchanged (approved | conditional | declined), so every reader
// downstream is untouched. The broker now picks approved or declined, and
// conditional is derived from whether a condition is there.
//
// THE ONE CARRY-FORWARD. Live data holds a row recorded as conditional with no
// condition text. Under the derivation alone it would re-save as approved,
// silently rewriting what the carrier was recorded as saying. So an untouched
// row that LOADED as conditional stays conditional; picking a decision again
// (which sets `touched`) is him overruling it, and that wins.
export function encodeApprovalStatus(v) {
    // A DECLINE IS NOT AN EXCLUSION (David, 2026-09-30). Declined: not named,
    // may still fly under the OPW. Excluded: may not fly at all, prints on the
    // card, the application and the bound policy. Both pass through untouched.
    if (v.status === 'excluded') return 'excluded';
    if (v.status === 'declined') return 'declined';
    if ((v.condition_text || '').trim()) return 'conditional';
    if (!v.touched && v.loaded_status === 'conditional') return 'conditional';
    return 'approved';
}

// What each step writes. Only the step's own keys, so advancing through TRIA
// cannot disturb a deductible and a PATCH that never mentions a field leaves it
// alone (mapBodyToQuote omits undefined keys).
//
// `carrier` appears in NO branch. That is the point, not an oversight.
// The TRIA half of the coverage save. The three legal states, and the server
// validator (assertTriaAnswerShape) refuses anything else with a sentence rather
// than a 23514. Unanswered sends NOTHING, so an untouched TRIA block cannot
// write a null over an answer or an answer over a blank.
//
// Its own function rather than inline in the terms case so the rule survives the
// page move it just went through: the shape is pinned by
// tests/quote-tria-answer.test.js and it should be findable by name.
function triaBody(f) {
  if (!TRIA_STATUSES.includes(f.tria_status)) return {};
  return f.tria_status === 'optional'
    ? { tria_status: f.tria_status, tria_premium: toNumber(f.tria_premium) }
    : { tria_status: f.tria_status, tria_premium: null };
}

export function bodyForStep(stepKey, f, rows, pilots) {
  switch (stepKey) {
    // The header block: the money, the reference and the two dates, which is one
    // lookup on the page rather than two steps.
    case 'offer': {
      // On a fleet the rows are the truth, so the SUM is what gets saved, not
      // whatever the header box happens to still be holding from before the
      // tails were filled in. That stale figure is the one that would reach a
      // client statement.
      const derived = derivedTaxesAndFees(rows);
      // ON A GRID THE PREMIUM IS DERIVED, SO THIS STEP MUST NOT WRITE IT.
      // A quote whose limits branch prices each combination as hull + the
      // liability leg + the medical leg, and the offer step's single Premium box
      // speaks for whichever corner happens to be primary. Sending it would make
      // that box a SECOND writer of a figure the axes own: type 9,000 here and
      // the next limit edit recomputes it back to 8,932 with nothing saying so.
      // One editable home, and on a grid it is the legs.
      const premiumIsDerived = usesAxes(f);
      return {
        // NEVER DEFAULTED. An empty label is sent as an empty string, which
        // mapBodyToOption drops rather than storing, so an option the broker did
        // not name stays unnamed. Writing the terms into it because the field
        // was blank would be a placeholder spelled like an answer.
        option_label: f.option_label ?? '',
        ...(premiumIsDerived ? {} : { premium: toNumber(f.premium) }),
        estimated_taxes: derived ? derived.estimated_taxes : toNumber(f.estimated_taxes),
        admin_fee: derived ? derived.admin_fee : toNumber(f.admin_fee),
        aircraft_schedule: schedulePayload(rows),
        // null, not ''. A field the broker walked past without filling in is
        // unanswered, and writing '' onto a NULL column turns "never said" into
        // "explicitly cleared". Deleting a real entry still clears it.
        carrier_quote_number: f.quote_number.trim() || null,
        proposed_effective: f.proposed_effective || undefined,
        // Always sent, even '', so clearing the offer lapse date clears it.
        quote_expires_at: f.quote_expires_at,
        // THE TERM MOVED HERE from the rest step, whose hint reads "Everything
        // else. All optional." A term is not optional and it is not everything
        // else: it is half of what the carrier came back with, it decides the
        // policy end date, and burying it at the bottom of the last step is
        // part of why Black Bear Aero's six-month policy printed a year.
        //
        // It writes the PRIMARY OPTION's term (mapBodyToOption), and the engine
        // derives quotes.term_months from that. One editable home.
        term_months: f.term_months || undefined,
      };
    }
    case 'terms': {
      // ── THE LIMITS AS AXES (2026-08-07) ──────────────────────────────────
      //
      // On a quote whose limits BRANCH, the per-limit fields stop being one
      // answer each and become the two lists, and the server generates the
      // combinations. They are therefore omitted from the body here: sending
      // both would be one save writing the limits twice, once as a scalar onto
      // the resolved primary and once as the grid, and those two would diverge
      // on exactly the corner nobody was looking at.
      //
      // `axesChanged` gates it because every step of this wizard sends every
      // field it holds. The corner set is the one thing on the page a stale
      // save DELETES rows from.
      const gridded = usesAxes(f) && axesChanged(f);
      const perLimit = gridded ? {
        // `confirm_withdraw` is NOT built here. The broker's yes to pulling
        // combinations the client can currently see is attached to the ONE
        // retry that asked for it (QuoteWizardSheet's saveStep `extra`), so it
        // cannot be parked on the form and authorise a later withdrawal he was
        // never asked about.
        axes: { liability: liabilityLegsOf(f), medical: medicalLegsOf(f) },
      } : {
        liability_per_occurrence: toNumber(f.liability_per_occurrence),
        // A smooth limit HAS no sublimit, so it clears one. An unanswered
        // smooth/split leaves both columns exactly as they were.
        ...(f.liability_smooth === null ? {} : {
          liability_smooth: f.liability_smooth,
          liability_per_passenger: f.liability_smooth ? null : toNumber(f.liability_per_passenger),
        }),
        medical_per_person: toNumber(f.medical_per_person),
        liability_premium: toNumber(f.liability_premium),
        medical_premium: toNumber(f.medical_premium),
      };
      return {
        coverage_type: f.coverage_type || null,
        aircraft_usage: f.aircraft_usage || null,
        ...perLimit,
        // THE LEGS MOVED HERE WITH THEIR FIELDS (2026-08-07). They were sent by
        // the OFFER step because that is where they were rendered; rendering
        // them on Coverage without moving this would have been a field that
        // saves nothing, which is the inverse of the bug that wiped David's $60
        // med-pay premium and just as silent.
        //
        // AND MOVING THEM OFF THE OFFER STEP CLOSES THAT BUG: the offer save no
        // longer carries medical_premium at all, so advancing through it can no
        // longer blank a value the carrier stated.
        //
        // The HULL premium stays here in both modes: it is shared by every
        // corner of a grid (deriveAxes refuses a set that disagrees on it) and
        // it is the base every combination is built on. The liability and
        // medical legs are in `perLimit` above, because those are the axes.
        hull_premium: toNumber(f.hull_premium),
        in_motion_deductible: f.in_motion_deductible || undefined,
        non_motion_deductible: f.non_motion_deductible || undefined,
        territory: f.territory.trim() || undefined,
        aircraft_schedule: schedulePayload(rows),
        // TRIA MOVED INTO THIS BODY WITH ITS PAGE (2026-08-01). Folding the step
        // into the coverage page without moving its keys here would have left
        // three buttons on screen that saved nothing, which is worse than the
        // page turn it replaced.
        //
        // On a grid the STATUS is shared and the AMOUNT rides the liability leg
        // (Allianz priced 675 against $20M and 840 against $25M). The server
        // strips tria_premium out of the shared half for exactly that reason, so
        // this body cannot flatten two real amounts into one whatever it sends.
        ...triaBody(f),
      };
    }
    case 'pilots': {
      // The pilot list now carries the WHOLE roster, including pilots who
      // cannot be offered (no CRM Contact). They render so nobody is silently
      // dropped, but nothing about the quote is written against them: there is
      // no record to write it to, and a requirement block for a name the quote
      // cannot name would be a claim with no subject.
      const offerable = pilots.filter((p) => p && p.offerable !== false);
      const commonRows = (Array.isArray(f.common_requirements) ? f.common_requirements : [])
        .map((r) => ({
          tail_number: String(r?.tail_number || '').trim().toUpperCase(),
          requirement: String(r?.requirement || '').trim(),
        }))
        .filter((r) => r.requirement);
      return {
        named_pilots_only: f.named_pilots_only,
        opw_certificate: f.named_pilots_only ? undefined : (f.opw_certificate || null),
        opw_ratings: f.named_pilots_only ? undefined : f.opw_ratings,
        opw_total_hours: f.named_pilots_only ? undefined : toNumber(f.opw_total_hours),
        opw_make_model_hours: f.named_pilots_only ? undefined : toNumber(f.opw_make_model_hours),
        opw_type_hours: f.named_pilots_only ? undefined : (f.opw_type_hours.trim() || null),
        opw_full_text: f.named_pilots_only ? undefined : (f.opw_full_text.trim() || null),
        // Encoded against the deal's pilots PLUS any name the stored text already
        // carried. encodePilotRequirements only emits blocks for names it is
        // given, so encoding against the roster alone would silently drop the
        // requirement of a pilot who has since left it. Flag, do not drop.
        // Shared requirements, structured. Sent ALWAYS, including as an empty
        // array: [] is the broker clearing the shared block and is a different
        // answer from never having set one, which is what NULL means. Without
        // that distinction, clearing the last row would fall back to the stale
        // rollup still sitting in the text and resurrect what was just deleted.
        common_requirements: commonRows,
        pilot_requirements: encodePilotRequirements(
          // The common region is DERIVED from the structured rows, never typed,
          // so the column and the text cannot drift apart.
          buildCommonRollup(commonRows), f.pilot_requirements_by_name,
          [
            ...offerable,
            ...Object.keys(f.pilot_requirements_by_name)
              .filter((n) => !offerable.some((p) => p && p.name === n))
              .map((name) => ({ name })),
          ]
        ) || null,
        // Always sent, even empty, so clearing a decision persists. Only pilots
        // carrying a status emit a row.
        // ONE per-pilot requirement box, and it is the one that PRINTS.
        // condition_text used to be a second place to type, and it was the one
        // that went nowhere: nothing outside the entry sheets reads it, while
        // the binding application reads pilot_requirements. A 564-character
        // Acceleration training requirement for Ruben Arellano Lopez sat in it
        // and never reached ND-APP-00233, which is signed and paid.
        //
        // So condition_text is now a MIRROR, written FROM the requirement, and
        // the conditional derivation reads that same text. The two cannot
        // disagree because there is only one source. The column stays for now
        // so nothing loses data; it can go once no reader is left.
        pilot_approval_states: Object.entries(f.pilot_approval_by_contact)
          .filter(([, v]) => v && v.status)
          .map(([contact_id, v]) => {
            const req = (f.pilot_requirements_by_name[v.name || ''] || '').trim();
            // The requirement box is the only place to TYPE, but an empty box
            // must never ERASE stored condition_text. Six live quotes carry
            // text there and nowhere else; wiping it on the next save would
            // destroy the very data the migration exists to rescue, including
            // two on bound deals. Write from the requirement when there is one,
            // otherwise leave what is stored exactly as it was.
            const cond = req || (v.condition_text || '').trim();
            const status = encodeApprovalStatus({ ...v, condition_text: cond });
            // Tails ride only on a named pilot: a whole-policy exclusion or a
            // decline has nothing per aircraft to say.
            const tails = (status === 'excluded' || status === 'declined') ? []
              : [...new Set((v.excluded_tails || []).map((t) => String(t || '').trim().toUpperCase()).filter(Boolean))];
            return {
              contact_id: String(contact_id),
              name: v.name || '',
              tail_number: '',
              status,
              condition_text: cond || undefined,
              ...(tails.length ? { excluded_tails: tails } : {}),
            };
          })
          // The name-only exclusions ride beside the roster's rows. A blank
          // name is a row he never finished and is not sent.
          .concat((f.carrier_excluded_names || [])
            .filter((x) => x && String(x.name || '').trim())
            .map((x) => ({
              contact_id: '',
              name: String(x.name).trim(),
              tail_number: '',
              status: 'excluded',
              condition_text: String(x.condition_text || '').trim() || undefined,
            }))),
      };
    }
    case 'rest':
      return {
        underwriter: f.underwriter.trim() || undefined,
        // Always sent, even '', so clearing the textarea clears the Tower card
        // and CRM too ('' = explicitly cleared, NULL = legacy/unknown).
        coverage_enhancements: f.coverage_enhancements.trim(),
        subject_to: f.subject_to.trim(),
        broker_notes: f.broker_notes.trim() || undefined,
        coverage_parts: f.coverage_parts.filter((r) => r.part_type).map((r) => {
          const out = { part_type: r.part_type, key: coveragePartKey(r) };
          if (r.id) out.id = r.id;
          const l = toNumber(r.limit); if (l != null) out.limit = l;
          const s = toNumber(r.sub_limit); if (s != null) out.sub_limit = s;
          if (r.broker_notes) out.broker_notes = r.broker_notes;
          if (r.client_blurb) out.client_blurb = r.client_blurb;
          out.optional = r.optional === true;
          if (r.optional) out.elected_default = r.elected_default === true;
          if (r.price_mode === 'liability_premium' || r.price_mode === 'hull_premium') {
            out.premium_pct_of = r.price_mode;
            const p = toNumber(r.pct);
            out.pct = p != null ? p / 100 : 0;
          } else {
            const prem = toNumber(r.premium);
            if (prem != null) out.premium = prem;
          }
          return out;
        }),
      };
    default:
      return {};
  }
}

// The form, scored. Mapped onto the completeness function's documented input
// shape so the wizard and the present sheet grade the same quote identically.
export function completenessInput(f, rows, pilots) {
  return {
    // Named roster pilots that cannot be offered (no CRM Contact, so no record
    // to carry a carrier decision against). Present ONLY when the caller passed
    // a roster: quoteCompleteness treats an absent key as "not asked", which is
    // what keeps PresentQuotesSheet unaffected.
    ...(Array.isArray(pilots)
      ? { unofferable_pilots: pilots.filter((p) => p && p.offerable === false).map((p) => p.name || 'Unnamed pilot') }
      : {}),
    premium: toNumber(f.premium),
    quote_number: f.quote_number,
    proposed_effective: f.proposed_effective,
    coverage_type: f.coverage_type,
    aircraft_usage: f.aircraft_usage,
    liability_per_occurrence: toNumber(f.liability_per_occurrence),
    liability_per_passenger: toNumber(f.liability_per_passenger),
    liability_smooth: f.liability_smooth,
    // The per-line hulls when the quote has lines; the policy-level figure when
    // it does not (hullAnswered prefers the lines and falls back to this one).
    hull_value: toNumber(f.hull_value),
    aircraft: rows.map((r) => ({ hull_value: r.hull_value === '' ? null : toNumber(r.hull_value) })),
    in_motion_deductible: f.in_motion_deductible,
    non_motion_deductible: f.non_motion_deductible,
    tria_status: f.tria_status,
    tria_premium: toNumber(f.tria_premium),
    named_pilots_only: f.named_pilots_only,
    opw_certificate: f.opw_certificate,
    opw_ratings: f.opw_ratings,
    opw_total_hours: f.opw_total_hours,
    opw_type_hours: f.opw_type_hours,
    opw_make_model_hours: f.opw_make_model_hours,
    opw_full_text: f.opw_full_text,
  };
}

